// Types
import { useEffect, type FC } from "react";

// Base
import { CountryDropdown } from "react-country-region-selector";

// Store
import { useProductData } from "@/zustand/productStore";

// Assets
import { DropdownArrow } from "@/assets/Icons";

interface CountryFieldProps {
  value?: string;
  error?: string;
  name: string;
  handleChange: (value: string) => void;
  placeholder?: string;
  contrast?: boolean;
}

/**
 * Countries the active campaign will accept (DEV-3732).
 *
 * Empty means "unknown" - a CRM outage, a cache miss, or a campaign with no
 * list - and CountryDropdown treats an empty whitelist as no filtering at all,
 * so the shopper still sees every country. Konnektive still declines a
 * restricted one and the checkout shows the translated message, so failing
 * open here costs feedback quality rather than enforcement.
 */
const allowedCountriesFor = (allowedCountries?: {
  base?: string[];
}): string[] =>
  Array.isArray(allowedCountries?.base) ? allowedCountries.base : [];

const CountryField: FC<CountryFieldProps> = ({
  value,
  error,
  name,
  handleChange,
  placeholder,
  contrast,
}) => {
  const { country_code, allowed_countries } = useProductData() as any;

  const allowedCountries = allowedCountriesFor(allowed_countries);
  const currentValue = value?.toUpperCase() || country_code;
  const isFiltering = allowedCountries.length > 0;
  const isAllowed = !isFiltering || allowedCountries.includes(currentValue);

  // A value the filtered list does not contain matches no <option>, so the
  // select renders blank while form state still holds it - and the order goes
  // out with a country the campaign refuses. Reachable via a campaign default
  // that is off its own list, or email prefill writing straight to form state.
  useEffect(() => {
    if (isFiltering && !isAllowed) {
      handleChange("");
    }
  }, [isFiltering, isAllowed]);

  if (!country_code) return null;

  return (
    <div className="flex flex-col gap-1">
      <label
        className={`text-sm font-bold whitespace-nowrap ${
          contrast ? "text-cc-dark-gray" : "text-cc-gray"
        }`}
        htmlFor={name}
      >
        {placeholder}
      </label>
      <div className="relative">
        <CountryDropdown
          id="country"
          valueType="short"
          labelType="full"
          // CountryDropdown filters its options once, in the constructor - it
          // has no componentDidUpdate and no getDerivedStateFromProps, so a
          // whitelist that changes later is silently ignored. Keying on the
          // list forces a remount when the campaign changes.
          key={allowedCountries.join(",")}
          value={isAllowed ? currentValue : ""}
          whitelist={allowedCountries}
          name={name}
          autoComplete="country-name"
          className={`w-full appearance-none rounded-md border p-4 text-sm ${
            contrast ? "border-cc-gray-5" : "border-cc-gray-4"
          }`}
          style={{
            color: contrast ? "#333333" : "#0A0A0A",
          }}
          onChange={(val: string) => handleChange(val)}
        />

        <div className="pointer-events-none absolute top-1/2 right-4 flex -translate-y-1/2 items-center">
          <DropdownArrow />
        </div>
      </div>

      {error && <div className="mt-1 text-xs text-red-500">{error}</div>}
    </div>
  );
};

export default CountryField;
